import { createSelector } from '@ngrx/store';
import { TransactionStatus } from '@rd/types';
import { LoyaltyProgramCapabilities } from '@rd/types/loyalty-program-capability.type';
import {
  LoyaltyProgram,
  LoyaltyProgramCategory,
} from '@rd/types/loyalty-program.type';
import { getLoyaltyProgramState } from '../reducers';
import { LoyaltyProgramState } from '../reducers/loyalty-program.reducer';

/**
 *
 * Sorting loyalty program utility function since the selectors retrieve the loyalty programs
 * as an Object. We want to maintain the sort order from NN
 */
function sortLoyaltyProgram(
  options: { ascending: boolean } = { ascending: true },
): (a: LoyaltyProgram, b: LoyaltyProgram) => number {
  return (a, b) => {
    return options.ascending
      ? a.sortIndex - b.sortIndex
      : b.sortIndex - a.sortIndex;
  };
}

export function filterLoyaltyProgramsByCapabilities(
  loyaltyPrograms: LoyaltyProgram[],
  capabilities: LoyaltyProgramCapabilities[],
): LoyaltyProgram[] {
  const capabilitySet = new Set(capabilities);
  const programs = loyaltyPrograms.filter((loyaltyProgram: LoyaltyProgram) =>
    loyaltyProgram.capabilities.some((lpCapability) =>
      capabilitySet.has(lpCapability.category),
    ),
  );
  programs.sort(sortLoyaltyProgram());
  return programs;
}

export const getAirlineLoyaltyPrograms = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyPrograms = Object.values(state.loyaltyPrograms).filter(
      (loyaltyProgram) =>
        loyaltyProgram.category === LoyaltyProgramCategory.Airline,
    );
    loyaltyPrograms.sort(sortLoyaltyProgram());
    return filterLoyaltyProgramsByCapabilities(loyaltyPrograms, [
      LoyaltyProgramCapabilities.PointsTransfer,
    ]);
  },
);

export const getHotelLoyaltyPrograms = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyPrograms = Object.values(state.loyaltyPrograms).filter(
      (loyaltyProgram) =>
        loyaltyProgram.category === LoyaltyProgramCategory.Hotel,
    );
    loyaltyPrograms.sort(sortLoyaltyProgram());
    return filterLoyaltyProgramsByCapabilities(loyaltyPrograms, [
      LoyaltyProgramCapabilities.PointsTransfer,
    ]);
  },
);

export const getCashBackToAccountLoyaltyProgram = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyProgrammes = Object.values(state.loyaltyPrograms);
    const filteredLoyaltyPrograms = [];
    for (const loyaltyProgram of loyaltyProgrammes) {
      if (
        loyaltyProgram.category === LoyaltyProgramCategory.CashbackToAccount
      ) {
        filteredLoyaltyPrograms.push(loyaltyProgram);
      }
    }
    const filteredCashbackToAccount = filterLoyaltyProgramsByCapabilities(
      filteredLoyaltyPrograms,
      [LoyaltyProgramCapabilities.CashBackToAccount],
    );
    return filteredCashbackToAccount.length === 0
      ? null
      : filteredCashbackToAccount[0];
  },
);

export const getCashBackToCardLoyaltyProgram = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyProgrammes = Object.values(state.loyaltyPrograms);
    const filteredLoyaltyPrograms = [];
    for (const loyaltyProgram of loyaltyProgrammes) {
      if (loyaltyProgram.category === LoyaltyProgramCategory.CashbackToCard) {
        filteredLoyaltyPrograms.push(loyaltyProgram);
      }
    }
    const filteredCashbackToCard = filterLoyaltyProgramsByCapabilities(
      filteredLoyaltyPrograms,
      [LoyaltyProgramCapabilities.CashBackToCard],
    );
    return filteredCashbackToCard.length === 0
      ? null
      : filteredCashbackToCard[0];
  },
);

export const getCashBackToPlatformLoyaltyProgram = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyProgrammes = Object.values(state.loyaltyPrograms);
    const filteredLoyaltyPrograms = [];
    for (const loyaltyProgram of loyaltyProgrammes) {
      if (
        loyaltyProgram.category === LoyaltyProgramCategory.CashbackToPlatform
      ) {
        filteredLoyaltyPrograms.push(loyaltyProgram);
      }
    }
    const filteredCashbackToPlatform = filterLoyaltyProgramsByCapabilities(
      filteredLoyaltyPrograms,
      [LoyaltyProgramCapabilities.CashBackToPlatform],
    );
    return filteredCashbackToPlatform.length === 0
      ? null
      : filteredCashbackToPlatform[0];
  },
);

export const getCashBackLoyaltyPrograms = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyPrograms = Object.values(state.loyaltyPrograms);
    return filterLoyaltyProgramsByCapabilities(
      loyaltyPrograms.filter(
        (loyaltyProgram) =>
          loyaltyProgram.category ===
            LoyaltyProgramCategory.CashbackToAccount ||
          loyaltyProgram.category === LoyaltyProgramCategory.CashbackToCard ||
          loyaltyProgram.category === LoyaltyProgramCategory.CashbackToPlatform,
      ),
      [
        LoyaltyProgramCapabilities.CashBackToCard,
        LoyaltyProgramCapabilities.CashBackToAccount,
        LoyaltyProgramCapabilities.CashBackToPlatform,
      ],
    );
  },
);

export const getRetailLoyaltyPrograms = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => {
    const loyaltyPrograms = Object.values(state.loyaltyPrograms);

    return filterLoyaltyProgramsByCapabilities(
      loyaltyPrograms.filter(
        (loyaltyProgram) =>
          loyaltyProgram.category === LoyaltyProgramCategory.Retail,
      ),
      [LoyaltyProgramCapabilities.PointsTransfer],
    );
  },
);

export const getLoyaltyPrograms = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => state.loyaltyPrograms,
);

export const getLoyaltyProgramIndexError = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => Boolean(state.index.error),
);

export const getLoyaltyProgramIndexLoading = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) =>
    state.index.status === TransactionStatus.LOADING,
);

export const getLoyaltyProgramIndexSuccess = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) =>
    state.index.status === TransactionStatus.SUCCESS,
);

export const getLoyaltyProgramFilterCategory = createSelector(
  getLoyaltyProgramState,
  (state: LoyaltyProgramState) => state.filter.category,
);

export const getFilteredPointsTransferLoyaltyPrograms = (
  includeVoucher = false,
) =>
  createSelector(getLoyaltyProgramState, (state: LoyaltyProgramState) => {
    const loyaltyPrograms = state.loyaltyPrograms;
    const loyaltyProgramsArray = Object.values(loyaltyPrograms).sort(
      sortLoyaltyProgram(),
    );
    const newArray = loyaltyProgramsArray.filter((a) =>
      [
        LoyaltyProgramCategory.Airline,
        LoyaltyProgramCategory.Hotel,
        ...(includeVoucher ? [LoyaltyProgramCategory.Voucher] : []),
        LoyaltyProgramCategory.Others,
      ].includes(a.category),
    );
    const keyword = state.filter.keyword;
    const category = state.filter.category;
    const keywordFilter = (loyaltyProgram: LoyaltyProgram): boolean => {
      return loyaltyProgram?.name
        ?.toLowerCase()
        .includes(keyword?.toLowerCase());
    };
    const categoryFilter = (loyaltyProgram: LoyaltyProgram): boolean => {
      if (
        category === LoyaltyProgramCategory.CashbackToAccount ||
        category === LoyaltyProgramCategory.CashbackToCard ||
        category === LoyaltyProgramCategory.CashbackToPlatform
      ) {
        return false;
      }

      if (category === 'all') {
        return true;
      } else {
        return loyaltyProgram.category === category;
      }
    };

    return filterLoyaltyProgramsByCapabilities(
      newArray.filter((loyaltyProgram) => {
        return keywordFilter(loyaltyProgram) && categoryFilter(loyaltyProgram);
      }),
      [LoyaltyProgramCapabilities.PointsTransfer],
    );
  });
